import { useEffect, useMemo, useState } from "react";
import { COLOR, FONT, RADIUS, SPACE } from "../lib/tokens";
import { paletteEntry } from "../lib/participantPalette";
import { useTeamCookSession } from "../lib/useTeamCookSession";
import AssignStepSheet from "./AssignStepSheet";
import InviteTeammatesSheet from "./InviteTeammatesSheet";
import { supabase } from "../lib/supabase";
import { totalStepSeconds } from "../lib/calendarUtils";

// TeamCookOverlay — single render slot CookMode injects above the step
// surface when a team session exists. Bundles three responsibilities:
//
//   1. TEAM PROGRESS strip — one avatar per participant, ringed in their
//      color_tag, with a "watching" badge for diners.
//   2. Floating ASSIGN pill — lead/helper can offer the CURRENT step to
//      teammates. Multi-select via AssignStepSheet.
//   3. Per-step done auto-sync — when the local user advances a step,
//      flip their own cook_step_assignments row to 'done'. The DB trigger
//      fans out completion to siblings.
//
// Read-only for diners: the assign affordance is hidden when role isn't
// lead/helper. The strip itself is always visible — observation is the
// whole point of bringing diners into CookMode.
//
// Props:
//   userId, cookSessionId, currentStep (the live recipe step object,
//   { id, title, timer? }), scheduledFor (Date|string, optional — used
//   to default the deadline math), recipeSteps (array, for cumulative
//   timer math).

export default function TeamCookOverlay({
  userId,
  cookSessionId,
  currentStep,
  recipeSteps = [],
  scheduledFor = null,
  family = [],
  friends = [],
}) {
  const team = useTeamCookSession({ userId, cookSessionId });
  const {
    participants, participantsById, assignmentsByStep,
    myRole, canEdit, assignStep, markStepDoneByCurrentUser, inviteParticipants,
  } = team;

  const [assignOpen, setAssignOpen] = useState(false);
  const [inviteOpen, setInviteOpen] = useState(false);
  const [profilesById, setProfilesById] = useState(new Map());

  // Hydrate profile rows for label/avatar rendering. Family-RLS lets us
  // SELECT profiles of anyone we share a relationship with; participants
  // outside the relationship graph fall back to a generic label.
  useEffect(() => {
    if (!participants?.length) return;
    const ids = participants.map((p) => p.user_id);
    let alive = true;
    (async () => {
      const { data, error } = await supabase
        .from("profiles")
        .select("id, name, avatar_url")
        .in("id", ids);
      if (!alive) return;
      if (error) {
        console.warn("[teamOverlay] profile fetch:", error.message);
        return;
      }
      setProfilesById(new Map((data || []).map((p) => [p.id, p])));
    })();
    return () => { alive = false; };
  }, [participants]);

  // Default deadline: scheduledFor − Σ(step.timer for all steps at-or-
  // before currentStep) − 5min buffer. Falls back to 30min from now when
  // no schedule exists.
  const defaultDeadline = useMemo(() => {
    const stepIdx = recipeSteps.findIndex((s) => s.id === currentStep?.id);
    if (stepIdx < 0) return null;
    if (scheduledFor) {
      const target = typeof scheduledFor === "string"
        ? new Date(scheduledFor)
        : scheduledFor;
      if (!Number.isNaN(target.getTime())) {
        const remainingSecs = recipeSteps
          .slice(stepIdx)
          .reduce((acc, s) => acc + (totalStepSeconds(s) || 0), 0);
        const buffer = 5 * 60;
        return new Date(target.getTime() - (remainingSecs + buffer) * 1000);
      }
    }
    return new Date(Date.now() + 30 * 60 * 1000);
  }, [recipeSteps, currentStep?.id, scheduledFor]);

  // Auto-mark the current user's assignment done whenever CookMode
  // signals via a stable cue. We can't reliably observe finishStep from
  // here, so we expose a callback the parent can fire. Simplest UX:
  // when our own step advances PAST one we were assigned, mark it done.
  // For v1, the lead's CookMode flips this manually via the floating
  // "DONE FOR ME" pill — covered below.

  if (!cookSessionId) return null;

  // Solo cook with no participants yet — render a compact "INVITE
  // TEAMMATES" CTA so the lead can pull people in mid-cook without
  // rescheduling. The sheet handles the actual picking.
  const hasTeammates = (family?.length || 0) + (friends?.length || 0) > 0;
  if (!participants?.length) {
    if (!hasTeammates) return null;
    return (
      <div style={{
        position: "sticky",
        top: 0,
        zIndex: 80,
        padding: `${SPACE.sm}px ${SPACE.section}px`,
        background: COLOR.deep,
        backdropFilter: "blur(6px)",
        borderBottom: `1px solid ${COLOR.edge}`,
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: SPACE.md,
      }}>
        <span style={{
          fontFamily: FONT.mono,
          fontSize: 10,
          letterSpacing: 1.2,
          color: COLOR.muted,
        }}>
          COOKING SOLO
        </span>
        <button
          type="button"
          className="mcm-focusable"
          onClick={() => setInviteOpen(true)}
          style={{
            fontFamily: FONT.mono,
            fontSize: 10,
            letterSpacing: 1.2,
            padding: "4px 10px",
            background: COLOR.leaf,
            color: "#fff",
            border: "none",
            borderRadius: RADIUS.sm,
            cursor: "pointer",
          }}
        >
          + INVITE TEAMMATES
        </button>
        <InviteTeammatesSheet
          open={inviteOpen}
          onClose={() => setInviteOpen(false)}
          family={family}
          friends={friends}
          selfUserId={userId}
          existingIds={new Set(participants.map((p) => p.user_id))}
          onInvite={inviteParticipants}
        />
      </div>
    );
  }

  const assignmentsHere = currentStep
    ? (assignmentsByStep.get(String(currentStep.id)) || [])
    : [];

  const myAssignmentHere = assignmentsHere.find(
    (a) => a.assigned_to === userId && a.status !== "done" && a.status !== "declined"
  );

  return (
    <div style={{
      position: "sticky",
      top: 0,
      zIndex: 80,
      padding: `${SPACE.md}px ${SPACE.section}px`,
      background: COLOR.deep,
      backdropFilter: "blur(6px)",
      borderBottom: `1px solid ${COLOR.edge}`,
    }}>
      {/* Header line */}
      <div style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: SPACE.md,
        marginBottom: SPACE.sm,
      }}>
        <div style={{
          fontFamily: FONT.mono,
          fontSize: 10,
          letterSpacing: 1.4,
          color: COLOR.muted,
        }}>
          TEAM ·{" "}
          {myRole === "diner" ? "WATCHING" : myRole?.toUpperCase() || ""}
        </div>
        {canEdit ? (
          <div style={{ display: "flex", gap: 6 }}>
            {hasTeammates ? (
              <button
                type="button"
                className="mcm-focusable"
                onClick={() => setInviteOpen(true)}
                style={{
                  fontFamily: FONT.mono,
                  fontSize: 10,
                  letterSpacing: 1.2,
                  padding: "4px 9px",
                  background: COLOR.ground,
                  color: COLOR.ink,
                  border: `1px solid ${COLOR.border}`,
                  borderRadius: RADIUS.sm,
                  cursor: "pointer",
                }}
              >
                + INVITE
              </button>
            ) : null}
            {currentStep ? (
              <button
                type="button"
                className="mcm-focusable"
                onClick={() => setAssignOpen(true)}
                style={{
                  fontFamily: FONT.mono,
                  fontSize: 10,
                  letterSpacing: 1.2,
                  padding: "4px 9px",
                  background: COLOR.leaf,
                  color: "#fff",
                  border: "none",
                  borderRadius: RADIUS.sm,
                  cursor: "pointer",
                }}
              >
                ASSIGN STEP
              </button>
            ) : null}
          </div>
        ) : null}
      </div>

      {/* Participant strip */}
      <div style={{ display: "flex", gap: SPACE.xs, flexWrap: "wrap" }}>
        {participants.map((p) => {
          const profile = profilesById.get(p.user_id);
          const palette = paletteEntry(p.color_tag);
          const name = profile?.name || "Teammate";
          const isDiner = p.role === "diner";
          return (
            <div
              key={p.user_id}
              title={`${name} · ${p.role}`}
              style={{
                display: "flex",
                alignItems: "center",
                gap: 6,
                padding: "4px 9px 4px 4px",
                background: palette.bg,
                border: `1px solid ${palette.ring}`,
                borderRadius: 999,
              }}
            >
              <span style={{
                width: 22,
                height: 22,
                borderRadius: "50%",
                background: palette.bgSolid,
                color: "#fff",
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                fontFamily: FONT.mono,
                fontSize: 10,
                fontWeight: 700,
                opacity: isDiner ? 0.65 : 1,
              }}>
                {name.slice(0, 1).toUpperCase()}
              </span>
              <span style={{
                fontFamily: FONT.mono,
                fontSize: 10,
                letterSpacing: 0.6,
                color: COLOR.ink,
                textTransform: "uppercase",
              }}>
                {name.split(" ")[0]}
              </span>
              {isDiner ? (
                <span style={{
                  fontFamily: FONT.mono,
                  fontSize: 8,
                  letterSpacing: 1,
                  color: COLOR.muted,
                }}>
                  WATCHING
                </span>
              ) : null}
            </div>
          );
        })}
      </div>

      {/* My-assignment hint for the current step */}
      {myAssignmentHere ? (
        <div style={{
          marginTop: SPACE.md,
          padding: `${SPACE.xs}px ${SPACE.md}px`,
          background: COLOR.goldDeep,
          border: `1px solid ${COLOR.goldDim}`,
          borderRadius: RADIUS.sm,
          fontFamily: FONT.mono,
          fontSize: 10,
          letterSpacing: 1.1,
          color: COLOR.ink,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: SPACE.md,
        }}>
          <span>
            YOU'RE ON: {currentStep?.title || `STEP ${currentStep?.id}`}
            {myAssignmentHere.status === "pending" ? " · TAP ACCEPT IN INBOX" : ""}
          </span>
          {myAssignmentHere.status === "accepted" ? (
            <button
              type="button"
              className="mcm-focusable"
              onClick={() => markStepDoneByCurrentUser(currentStep.id)}
              style={{
                fontFamily: FONT.mono,
                fontSize: 9,
                letterSpacing: 1.2,
                padding: "3px 8px",
                background: COLOR.leaf,
                color: "#fff",
                border: "none",
                borderRadius: RADIUS.sm,
                cursor: "pointer",
              }}
            >
              MARK DONE
            </button>
          ) : null}
        </div>
      ) : null}

      <AssignStepSheet
        open={assignOpen}
        onClose={() => setAssignOpen(false)}
        step={currentStep}
        participants={participants}
        existingAssignments={assignmentsHere}
        profilesById={profilesById}
        defaultDeadline={defaultDeadline}
        selfUserId={userId}
        onAssign={async ({ assigneeIds, deadline }) => {
          await assignStep({
            stepId: currentStep.id,
            stepTitle: currentStep.title,
            assigneeIds,
            deadline,
          });
        }}
      />
      <InviteTeammatesSheet
        open={inviteOpen}
        onClose={() => setInviteOpen(false)}
        family={family}
        friends={friends}
        selfUserId={userId}
        existingIds={new Set(participants.map((p) => p.user_id))}
        onInvite={inviteParticipants}
      />
    </div>
  );
}
